import React from "react";
import { Box, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view";
import { useTreeViewContext } from "../TreeViewContext";
import { sectionItemNodeId } from "../utils/treeNodeIds";
import { designAutomationWbs } from "../utils/treeConstants";
import { TreeNodeIcon } from "./TreeNodeIcon";
import { WbsNode } from "./WbsNode";

/**
 * One "Design Automation" row, holding the WBS that belong to it.
 *
 * These are the WBS the Estimation section leaves out - the ones written
 * under a legacy type by the automation software - so they are listed where
 * they came from instead of nowhere at all. A row whose discipline has no
 * such WBS is a plain leaf, exactly like the rows that name no discipline
 * (Smart P&ID, Line List).
 */
export const DesignAutomationItemNode = ({ plant, sectionKey, item }) => {
  const { treeData } = useTreeViewContext();
  const id = sectionItemNodeId(plant.id, sectionKey, item.key);

  const wbsList = treeData.wbsByPlant[plant.id];
  const ownWbs = designAutomationWbs(wbsList, item.key).filter((wbs) =>
    treeData.isWbsVisible(plant, wbs),
  );

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<TreeNodeIcon />}
      label={
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
        >
          <Typography
            variant="body2"
            noWrap
            title={item.label}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {item.label}
          </Typography>
          {ownWbs.length > 0 && (
            <Typography variant="caption" color="text.disabled">
              ({ownWbs.length})
            </Typography>
          )}
        </Box>
      }
    >
      {ownWbs.map((wbs) => (
        // No "Add Rev" or "Add Document", and the revision/Spec rows still
        // open nothing: a revision listed here is not the Estimation
        // revision the same row stands for in the Estimation section. The
        // 3D Modeling branch under the WBS is the exception - its BOM
        // drawings open the BomPage they belong to (see ModelingNodes).
        <WbsNode
          key={wbs.id}
          plant={plant}
          wbs={wbs}
          allowAdd={false}
          allowOpen={false}
          showModelingBranch
        />
      ))}
    </TreeItem>
  );
};

export default DesignAutomationItemNode;
